// The ? button in a module's header (left of the pin) and its card: "what am I looking at?" in
// plain words (Ben, 2026-10-17; the text comes from `explain.ts`). Portalled into the header the
// shell renders (PlacedModule), so any widget can add one. Live only for now.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getPlayerShortName } from "../data/player-utils.js";
import { playerById, type PlayerId } from "../data/players.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { pickedPlayer } from "../live/picked-players.js";

/** A player's short name for an explanation, or "the player" when we don't know it here (a
 *  group's reference player may be neither in the snapshot nor picked in this session). */
export function shortNameOrPlayer(id: PlayerId | null): string {
  const p = id === null ? undefined : (playerById(snapshotPlayersOrNone(), id) ?? pickedPlayer(id));
  return p ? getPlayerShortName(p) : "the player";
}

/** The press that closes the card does nothing else (Ben, 2026-10-17: a click on a module header
 *  only closes it, it doesn't select the module). Swallows the rest of that press — its
 *  mousedown, pointerup, mouseup and click — then lets presses through again. */
function swallowRestOfPress(): void {
  const types = ["mousedown", "pointerup", "mouseup", "click"] as const;
  const stop = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.type === "click") done();
  };
  const done = () => {
    for (const t of types) window.removeEventListener(t, stop, true);
    window.clearTimeout(timer);
  };
  for (const t of types) window.addEventListener(t, stop, true);
  // A press that never ends in a click (dragged off) must not swallow the next one.
  const timer = window.setTimeout(done, 1000);
}

export function ModuleExplainer({ mod, paragraphs }: { mod: HTMLElement; paragraphs: readonly string[] }) {
  const [slot] = useState(() => {
    const s = document.createElement("div");
    s.className = "module-explain-slot";
    return s;
  });
  // Where the card sits (under the ?, right edges lined up), or null when closed. The header clips
  // what overflows it, so the card floats on the page (fixed) instead of inside the header.
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const open = at !== null;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Into the header, left of the pin (first in .header-actions).
  useEffect(() => {
    const actions = mod.querySelector(":scope > .module-header .header-actions");
    if (!actions) return;
    actions.insertBefore(slot, actions.firstChild);
    return () => slot.remove();
  }, [mod, slot]);

  // A click anywhere else, a scroll/zoom of the canvas or a resize closes it (it would float off).
  useEffect(() => {
    if (!open) return;
    const close = () => setAt(null);
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (slot.contains(t) || cardRef.current?.contains(t)) return;
      e.stopPropagation();
      e.preventDefault();
      swallowRestOfPress();
      close();
    };
    // Pointerdown, captured on the window: it reaches us before the canvas and modules see it,
    // so the closing press can be stopped there.
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("wheel", close, { passive: true });
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("wheel", close);
      window.removeEventListener("resize", close);
    };
  }, [open, slot]);

  const toggle = () => {
    if (open || !buttonRef.current) return setAt(null);
    const r = buttonRef.current.getBoundingClientRect();
    setAt({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right - 4) });
  };

  return createPortal(
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`module-explain${open ? " open" : ""}`}
        title="What am I looking at?"
        aria-expanded={open}
        // The header starts a module drag on press; a press here is a click.
        onPointerDown={(e) => e.stopPropagation()}
        onClick={toggle}
      >
        ?
      </button>
      {at &&
        createPortal(
          <div
            className="module-explain-card"
            ref={cardRef}
            style={{ top: at.top, right: at.right }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>,
          document.body,
        )}
    </>,
    slot,
  );
}
